import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, Briefcase, Clock, DollarSign, User, LogOut, Navigation,
  CheckCircle, MapPin, Phone, Calendar, ChevronRight, RefreshCw,
  Play, Pause, AlertCircle, Car, Package, ShoppingBag, Shirt,
  ArrowLeft, Menu, X, Bell, Check, XCircle, Volume2, VolumeX
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api, { workersAPI, clockAPI, jobsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import notificationSound, { playNewJobAlert, initSound } from '../services/notificationSound';
import { requestNotificationPermission, getNotificationPermission, showBookingNotification } from '../services/pushNotifications';
import NewJobPopup from '../components/worker/NewJobPopup';

// Service type icons
const SERVICE_ICONS = {
  rides: Car,
  courier: Package,
  errands: ShoppingBag,
  laundry: Shirt,
  default: Briefcase
};

// Status colors
const STATUS_COLORS = {
  pending: 'bg-yellow-500',
  assigned: 'bg-blue-500',
  in_progress: 'bg-purple-500',
  completed: 'bg-green-500',
  cancelled: 'bg-red-500'
};

const WorkerMobileApp = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // Core states
  const [activeTab, setActiveTab] = useState('home');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(
    localStorage.getItem('notification_sound_enabled') !== 'false'
  );
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState(getNotificationPermission());
  
  // Track previous job count for new job alerts
  const prevJobCountRef = useRef(0);
  
  // Data states
  const [profile, setProfile] = useState(null);
  const [todayJobs, setTodayJobs] = useState([]);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [clockStatus, setClockStatus] = useState(null);
  const [earnings, setEarnings] = useState({ today: 0, week: 0, pending: 0 });
  const [stats, setStats] = useState({ completed: 0, rating: 0, acceptance: 0 });
  
  // Selected job for detail view
  const [selectedJob, setSelectedJob] = useState(null);
  
  // New job popup state
  const [incomingJob, setIncomingJob] = useState(null);
  const wsRef = useRef(null);

  // Check notification permission and show prompt if needed
  useEffect(() => {
    const permission = getNotificationPermission();
    setNotificationStatus(permission);
    
    // Show prompt if not yet granted and not already dismissed
    if (permission === 'default' && !localStorage.getItem('worker_notification_prompt_dismissed')) {
      const timer = setTimeout(() => setShowNotificationPrompt(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Handle enabling notifications
  const handleEnableNotifications = async () => {
    const result = await requestNotificationPermission();
    setNotificationStatus(result);
    if (result === 'granted') {
      toast.success('Notifications enabled! You\'ll get alerts for new jobs.');
      setShowNotificationPrompt(false);
    } else if (result === 'denied') {
      toast.error('Notifications blocked. Enable in browser settings.');
      setShowNotificationPrompt(false);
    }
  };

  const dismissNotificationPrompt = () => {
    setShowNotificationPrompt(false);
    localStorage.setItem('worker_notification_prompt_dismissed', 'true');
  };

  // Initialize sound on first user interaction
  useEffect(() => {
    const handleInteraction = () => {
      initSound();
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
    window.addEventListener('click', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);
    return () => {
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, []);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [profileRes, jobsRes, clockRes, earningsRes] = await Promise.all([
        workersAPI.getProfile().catch(() => ({ data: null })),
        api.get('/bookings?status=assigned').catch(() => ({ data: [] })),
        clockAPI.getStatus().catch(() => ({ data: null })),
        api.get('/workers/earnings/summary').catch(() => ({ data: {} }))
      ]);
      
      setProfile(profileRes.data);
      
      // Filter jobs for this worker
      const myJobs = Array.isArray(jobsRes.data) 
        ? jobsRes.data.filter(j => j.worker_id === user?.id)
        : [];
      
      // Today's jobs
      const today = new Date().toISOString().split('T')[0];
      setTodayJobs(myJobs.filter(j => j.scheduled_date === today));
      
      setClockStatus(clockRes.data);
      
      if (earningsRes.data) {
        setEarnings({
          today: earningsRes.data.today || 0,
          week: earningsRes.data.this_week || 0,
          pending: earningsRes.data.pending || 0
        });
        setStats({
          completed: earningsRes.data.completed_jobs || 0,
          rating: earningsRes.data.rating || 0,
          acceptance: earningsRes.data.acceptance_rate || 0
        });
      }
      
      // Fetch available jobs
      const availRes = await api.get('/jobs/available').catch(() => ({ data: [] }));
      const newAvailableJobs = Array.isArray(availRes.data) ? availRes.data : [];
      
      // Play sound and show notification if new jobs available (and not first load)
      if (soundEnabled && prevJobCountRef.current > 0 && newAvailableJobs.length > prevJobCountRef.current) {
        playNewJobAlert();
        toast.success('New job available!', { duration: 3000 });
        
        // Show browser push notification
        const newJobsCount = newAvailableJobs.length - prevJobCountRef.current;
        showBookingNotification(
          `${newJobsCount} New Job${newJobsCount > 1 ? 's' : ''} Available!`,
          'Tap to view and accept the job',
          () => { window.focus(); setActiveTab('jobs'); }
        );
      }
      prevJobCountRef.current = newAvailableJobs.length;
      setAvailableJobs(newAvailableJobs);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.id, soundEnabled]);

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // WebSocket for real-time job notifications
  useEffect(() => {
    let reconnectTimeout;
    
    const connectWebSocket = async () => {
      try {
        // Get WebSocket token
        const response = await fetch(`${window.location.origin}/api/auth/ws-token`, {
          credentials: 'include'
        });
        
        if (!response.ok) return;
        
        const data = await response.json();
        const token = data.ws_token;
        if (!token) return;

        // Connect to WebSocket
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${wsProtocol}//${window.location.host}/api/ws/notifications?token=${token}`;
        
        wsRef.current = new WebSocket(wsUrl);
        
        wsRef.current.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            
            // Handle new job/ride assignment offers
            if (msg.type === 'notification' && msg.data) {
              const notif = msg.data;
              if (notif.notification_type === 'new_job_available' || 
                  notif.notification_type === 'ride_request' ||
                  notif.notification_type === 'job_offer') {
                // Show the job popup
                setIncomingJob(notif.job || notif.booking || notif.data);
              }
            }
            
            // Handle direct job assignment
            if (msg.type === 'job_offer' || msg.type === 'new_ride_request') {
              setIncomingJob(msg.job || msg.booking || msg.data);
            }
          } catch (e) {
            console.error('WS parse error:', e);
          }
        };
        
        wsRef.current.onclose = () => {
          // Reconnect after 5 seconds
          reconnectTimeout = setTimeout(connectWebSocket, 5000);
        };
      } catch (error) {
        console.error('WebSocket connection failed:', error);
        reconnectTimeout = setTimeout(connectWebSocket, 5000);
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, []);

  // Handle job accept
  const handleAcceptJob = async (job) => {
    const jobId = job?.id || job?.booking_id;
    if (!jobId) throw new Error('Invalid job');
    
    // Try to claim/accept the job
    const response = await api.post(`/jobs/${jobId}/claim`).catch(() => 
      api.put(`/bookings/${jobId}/assign`, { worker_id: user?.id })
    );
    
    if (response.data) {
      fetchData(); // Refresh jobs list
    }
  };

  // Handle job schedule (accept for later)
  const handleScheduleJob = async (job) => {
    const jobId = job?.id || job?.booking_id;
    if (!jobId) throw new Error('Invalid job');
    
    // Accept but mark as scheduled
    await api.post(`/jobs/${jobId}/claim`, { scheduled: true }).catch(() =>
      api.put(`/bookings/${jobId}/assign`, { worker_id: user?.id, scheduled: true })
    );
    
    fetchData();
  };

  // Handle job decline
  const handleDeclineJob = (job) => {
    // Just close the popup - job goes back to available pool
    console.log('Job declined:', job?.id);
  };

  // Pull to refresh
  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Clock In/Out
  const handleClockToggle = async () => {
    try {
      if (clockStatus?.is_clocked_in) {
        await clockAPI.clockOut();
        toast.success('Clocked out successfully');
      } else {
        // Get GPS for clock in
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              await clockAPI.clockIn({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              });
              toast.success('Clocked in successfully');
              fetchData();
            },
            () => {
              toast.error('Please enable GPS to clock in');
            }
          );
          return;
        }
        await clockAPI.clockIn();
        toast.success('Clocked in successfully');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update clock status');
    }
  };

  // Accept/Decline job
  const handleJobAction = async (jobId, action) => {
    try {
      if (action === 'accept') {
        await jobsAPI.claimJob(jobId);
        toast.success('Job accepted!');
      } else {
        await api.put(`/bookings/${jobId}/decline`);
        toast.success('Job declined');
      }
      fetchData();
      setSelectedJob(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} job`);
    }
  };

  // Start navigation
  const handleNavigate = (address) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encoded}`, '_blank');
  };

  // Call customer
  const handleCall = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  // Start job
  const handleStartJob = async (jobId) => {
    try {
      await api.put(`/bookings/${jobId}/status`, { status: 'in_progress' });
      toast.success('Job started');
      fetchData();
    } catch (error) {
      toast.error('Failed to start job');
    }
  };

  // Complete job
  const handleCompleteJob = async (jobId) => {
    try {
      await api.put(`/bookings/${jobId}/status`, { status: 'completed' });
      toast.success('Job completed!');
      fetchData();
      setSelectedJob(null);
    } catch (error) {
      toast.error('Failed to complete job');
    }
  };

  // Logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const ServiceIcon = ({ type }) => {
    const Icon = SERVICE_ICONS[type] || SERVICE_ICONS.default;
    return <Icon className="w-5 h-5" />;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-3 border-[#D4AF37] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pb-20" data-testid="worker-mobile-app">
      {/* Background Glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#D4AF37]/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-40 left-0 w-[250px] h-[250px] bg-emerald-500/5 rounded-full blur-[80px]" />
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-40 sticky-header px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#E6C965] flex items-center justify-center text-black font-bold shadow-lg shadow-[#D4AF37]/20">
              {user?.full_name?.charAt(0) || 'W'}
            </div>
            <div>
              <p className="font-semibold text-sm text-white">{user?.full_name || 'Worker'}</p>
              <p className="text-xs text-slate-400">
                {clockStatus?.is_clocked_in ? (
                  <span className="text-emerald-400 flex items-center gap-1">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                    On Duty
                  </span>
                ) : (
                  <span className="text-slate-500">Off Duty</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => {
                const newValue = !soundEnabled;
                setSoundEnabled(newValue);
                notificationSound.setEnabled(newValue);
                toast.success(newValue ? 'Job alerts ON' : 'Job alerts muted');
              }}
              className={`p-2 rounded-full hover:bg-white/10 ${soundEnabled ? 'text-[#D4AF37]' : 'text-white/40'}`}
              title={soundEnabled ? 'Mute job alerts' : 'Enable job alerts'}
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            <button 
              onClick={handleRefresh}
              className={`p-2 rounded-full hover:bg-white/10 ${refreshing ? 'animate-spin' : ''}`}
            >
              <RefreshCw className="w-5 h-5 text-white/60" />
            </button>
            <button 
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 rounded-full hover:bg-white/10"
            >
              {showMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {showMenu && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-16 right-4 z-50 bg-[#1a1a1a] rounded-xl border border-white/10 shadow-xl overflow-hidden w-48"
          >
            <button 
              onClick={() => { setActiveTab('profile'); setShowMenu(false); }}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 text-left"
            >
              <User className="w-4 h-4 text-[#D4AF37]" />
              <span>My Profile</span>
            </button>
            <button 
              onClick={() => navigate('/worker/dashboard')}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 text-left"
            >
              <Briefcase className="w-4 h-4 text-[#D4AF37]" />
              <span>Full Dashboard</span>
            </button>
            <hr className="border-white/10" />
            <button 
              onClick={handleLogout}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-red-500/10 text-red-400 text-left"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Push Notification Permission Prompt */}
      <AnimatePresence>
        {showNotificationPrompt && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mx-4 mb-4 mt-2 p-4 bg-gradient-to-r from-[#D4AF37]/20 to-emerald-500/20 rounded-xl border border-[#D4AF37]/30"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[#D4AF37]/20 rounded-lg">
                <Bell className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white text-sm">Enable Instant Job Alerts</h3>
                <p className="text-xs text-white/60 mt-1">Get notified immediately when new jobs are available, even when the app is in the background.</p>
                <div className="flex gap-2 mt-3">
                  <Button
                    onClick={handleEnableNotifications}
                    className="bg-[#D4AF37] hover:bg-[#C4A030] text-black text-xs px-3 py-1 h-8"
                  >
                    Enable Notifications
                  </Button>
                  <Button
                    onClick={dismissNotificationPrompt}
                    variant="ghost"
                    className="text-white/60 hover:text-white text-xs px-3 py-1 h-8"
                  >
                    Maybe Later
                  </Button>
                </div>
              </div>
              <button onClick={dismissNotificationPrompt} className="text-white/40 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="px-4 py-4">
        <AnimatePresence mode="wait">
          {/* HOME TAB */}
          {activeTab === 'home' && (
            <motion.div
              key="home"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              {/* Clock In/Out Card */}
              <div className="bg-gradient-to-br from-[#1a1a1a] to-[#111] rounded-2xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-white/50 text-sm">Current Status</p>
                    <p className={`text-lg font-bold ${clockStatus?.is_clocked_in ? 'text-green-400' : 'text-white/60'}`}>
                      {clockStatus?.is_clocked_in ? 'Clocked In' : 'Clocked Out'}
                    </p>
                    {clockStatus?.is_clocked_in && clockStatus?.clock_in_time && (
                      <p className="text-xs text-white/40">
                        Since {new Date(clockStatus.clock_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={handleClockToggle}
                    className={`rounded-full w-16 h-16 ${
                      clockStatus?.is_clocked_in 
                        ? 'bg-red-500 hover:bg-red-600' 
                        : 'bg-green-500 hover:bg-green-600'
                    }`}
                  >
                    {clockStatus?.is_clocked_in ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                  </Button>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#1a1a1a] rounded-xl p-3 text-center border border-white/5">
                  <p className="text-xl font-bold text-[#D4AF37]">${earnings.today.toFixed(0)}</p>
                  <p className="text-xs text-white/50">Today</p>
                </div>
                <div className="bg-[#1a1a1a] rounded-xl p-3 text-center border border-white/5">
                  <p className="text-xl font-bold text-green-400">{todayJobs.length}</p>
                  <p className="text-xs text-white/50">Jobs Today</p>
                </div>
                <div className="bg-[#1a1a1a] rounded-xl p-3 text-center border border-white/5">
                  <p className="text-xl font-bold text-purple-400">{stats.rating > 0 ? stats.rating.toFixed(1) : '-'}</p>
                  <p className="text-xs text-white/50">Rating</p>
                </div>
              </div>

              {/* Today's Jobs */}
              <div>
                <h3 className="text-sm font-semibold text-white/70 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#D4AF37]" />
                  Today's Jobs
                </h3>
                {todayJobs.length === 0 ? (
                  <div className="bg-[#1a1a1a] rounded-xl p-6 text-center border border-white/5">
                    <Briefcase className="w-10 h-10 text-white/20 mx-auto mb-2" />
                    <p className="text-white/50 text-sm">No jobs scheduled for today</p>
                    <Button 
                      onClick={() => setActiveTab('jobs')}
                      className="mt-3 bg-[#D4AF37] text-black hover:bg-[#E6C965]"
                      size="sm"
                    >
                      Browse Available Jobs
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {todayJobs.map(job => (
                      <motion.div
                        key={job.id}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedJob(job)}
                        className="bg-[#1a1a1a] rounded-xl p-4 border border-white/5 cursor-pointer active:bg-white/5"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              job.status === 'in_progress' ? 'bg-purple-500/20 text-purple-400' : 'bg-[#D4AF37]/20 text-[#D4AF37]'
                            }`}>
                              <ServiceIcon type={job.service_type} />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{job.service_name || job.service_type}</p>
                              <p className="text-xs text-white/50">{job.scheduled_time}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-[#D4AF37] font-bold">${job.worker_pay || job.total_amount || 0}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[job.status]} text-white`}>
                              {job.status?.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2 text-xs text-white/50">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{job.address}, {job.city}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* JOBS TAB */}
          {activeTab === 'jobs' && (
            <motion.div
              key="jobs"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-[#D4AF37]" />
                Available Jobs
              </h2>
              
              {availableJobs.length === 0 ? (
                <div className="bg-[#1a1a1a] rounded-xl p-8 text-center border border-white/5">
                  <AlertCircle className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50">No jobs available right now</p>
                  <p className="text-xs text-white/30 mt-1">Check back soon!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableJobs.map(job => (
                    <motion.div
                      key={job.id}
                      whileTap={{ scale: 0.98 }}
                      className="bg-[#1a1a1a] rounded-xl p-4 border border-white/5"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                            <ServiceIcon type={job.service_type} />
                          </div>
                          <div>
                            <p className="font-medium">{job.service_name || job.service_type}</p>
                            <p className="text-xs text-white/50">{job.scheduled_date} • {job.scheduled_time}</p>
                          </div>
                        </div>
                        <p className="text-[#D4AF37] font-bold text-lg">${job.worker_pay || job.total_amount || 0}</p>
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs text-white/50 mb-3">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{job.city}, NC</span>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleJobAction(job.id, 'accept')}
                          className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                          size="sm"
                        >
                          <Check className="w-4 h-4 mr-1" /> Accept
                        </Button>
                        <Button
                          onClick={() => setSelectedJob(job)}
                          variant="outline"
                          className="flex-1 border-white/20"
                          size="sm"
                        >
                          View Details
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* EARNINGS TAB */}
          {activeTab === 'earnings' && (
            <motion.div
              key="earnings"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              <h2 className="text-lg font-bold flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                Earnings
              </h2>
              
              {/* Earnings Summary */}
              <div className="bg-gradient-to-br from-[#D4AF37]/20 to-[#1a1a1a] rounded-2xl p-5 border border-[#D4AF37]/30">
                <p className="text-white/50 text-sm">This Week</p>
                <p className="text-4xl font-bold text-[#D4AF37]">${earnings.week.toFixed(2)}</p>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-white/50">Today</p>
                    <p className="text-lg font-semibold">${earnings.today.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/50">Pending</p>
                    <p className="text-lg font-semibold text-yellow-400">${earnings.pending.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#1a1a1a] rounded-xl p-4 border border-white/5">
                  <p className="text-2xl font-bold text-green-400">{stats.completed}</p>
                  <p className="text-xs text-white/50">Jobs Completed</p>
                </div>
                <div className="bg-[#1a1a1a] rounded-xl p-4 border border-white/5">
                  <p className="text-2xl font-bold text-purple-400">{stats.acceptance > 0 ? `${stats.acceptance}%` : '-'}</p>
                  <p className="text-xs text-white/50">Acceptance Rate</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <motion.div
              key="profile"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              <h2 className="text-lg font-bold flex items-center gap-2">
                <User className="w-5 h-5 text-[#D4AF37]" />
                My Profile
              </h2>
              
              <div className="bg-[#1a1a1a] rounded-2xl p-5 border border-white/5 text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] mx-auto flex items-center justify-center text-black text-2xl font-bold mb-3">
                  {user?.full_name?.charAt(0) || 'W'}
                </div>
                <h3 className="text-xl font-bold">{user?.full_name}</h3>
                <p className="text-white/50 text-sm">{user?.email}</p>
                {profile?.phone && (
                  <p className="text-white/50 text-sm">{profile.phone}</p>
                )}
                <div className="mt-4 flex justify-center gap-2">
                  {profile?.is_verified && (
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">Verified</span>
                  )}
                  {profile?.service_types?.map(type => (
                    <span key={type} className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs rounded-full capitalize">
                      {type}
                    </span>
                  ))}
                </div>
              </div>
              
              <Button
                onClick={() => navigate('/worker/dashboard?tab=profile')}
                className="w-full bg-[#1a1a1a] border border-white/10 hover:bg-white/5"
              >
                Edit Full Profile
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
              
              <Button
                onClick={handleLogout}
                variant="destructive"
                className="w-full"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Job Detail Modal */}
      <AnimatePresence>
        {selectedJob && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-end"
            onClick={() => setSelectedJob(null)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="bg-[#111] w-full rounded-t-3xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Handle bar */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-white/20 rounded-full"></div>
              </div>
              
              <div className="px-5 pb-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
                      <ServiceIcon type={selectedJob.service_type} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">{selectedJob.service_name || selectedJob.service_type}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[selectedJob.status]} text-white`}>
                        {selectedJob.status?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-[#D4AF37]">${selectedJob.worker_pay || selectedJob.total_amount || 0}</p>
                </div>

                {/* Schedule */}
                <div className="bg-[#1a1a1a] rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-3 text-white/80">
                    <Calendar className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <p className="font-medium">{selectedJob.scheduled_date}</p>
                      <p className="text-sm text-white/50">{selectedJob.scheduled_time} • {selectedJob.duration_hours || 1} hr</p>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="bg-[#1a1a1a] rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium">{selectedJob.address}</p>
                      <p className="text-sm text-white/50">{selectedJob.city}, {selectedJob.state} {selectedJob.zip_code}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleNavigate(`${selectedJob.address}, ${selectedJob.city}, ${selectedJob.state} ${selectedJob.zip_code}`)}
                    className="w-full mt-3 bg-blue-500 hover:bg-blue-600"
                    size="sm"
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Get Directions
                  </Button>
                </div>

                {/* Customer */}
                {(selectedJob.customer_name || selectedJob.guest_name) && (
                  <div className="bg-[#1a1a1a] rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5 text-[#D4AF37]" />
                        <div>
                          <p className="font-medium">{selectedJob.customer_name || selectedJob.guest_name}</p>
                          <p className="text-sm text-white/50">Customer</p>
                        </div>
                      </div>
                      {(selectedJob.customer_phone || selectedJob.guest_phone || selectedJob.client_phone) && (
                        <Button
                          onClick={() => handleCall(selectedJob.customer_phone || selectedJob.guest_phone || selectedJob.client_phone)}
                          size="sm"
                          className="bg-green-500 hover:bg-green-600"
                        >
                          <Phone className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Special Instructions */}
                {selectedJob.special_instructions && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-4">
                    <p className="text-xs text-yellow-400 font-semibold mb-1">Special Instructions</p>
                    <p className="text-sm text-white/80">{selectedJob.special_instructions}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3 mt-6">
                  {selectedJob.status === 'assigned' && (
                    <Button
                      onClick={() => handleStartJob(selectedJob.id)}
                      className="w-full bg-purple-500 hover:bg-purple-600 h-12"
                    >
                      <Play className="w-5 h-5 mr-2" />
                      Start Job
                    </Button>
                  )}
                  
                  {selectedJob.status === 'in_progress' && (
                    <Button
                      onClick={() => handleCompleteJob(selectedJob.id)}
                      className="w-full bg-green-500 hover:bg-green-600 h-12"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Complete Job
                    </Button>
                  )}
                  
                  {selectedJob.status === 'pending' && !selectedJob.worker_id && (
                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        onClick={() => handleJobAction(selectedJob.id, 'accept')}
                        className="bg-green-500 hover:bg-green-600 h-12"
                      >
                        <Check className="w-5 h-5 mr-1" /> Accept
                      </Button>
                      <Button
                        onClick={() => handleJobAction(selectedJob.id, 'decline')}
                        variant="destructive"
                        className="h-12"
                      >
                        <XCircle className="w-5 h-5 mr-1" /> Decline
                      </Button>
                    </div>
                  )}
                  
                  <Button
                    onClick={() => setSelectedJob(null)}
                    variant="outline"
                    className="w-full border-white/20"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-[#111] border-t border-white/10 px-2 py-2 z-30">
        <div className="flex justify-around">
          {[
            { id: 'home', icon: Home, label: 'Home' },
            { id: 'jobs', icon: Briefcase, label: 'Jobs', badge: availableJobs.length },
            { id: 'earnings', icon: DollarSign, label: 'Earnings' },
            { id: 'profile', icon: User, label: 'Profile' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center py-2 px-4 rounded-xl transition-colors relative ${
                activeTab === tab.id 
                  ? 'text-[#D4AF37]' 
                  : 'text-white/50 hover:text-white/70'
              }`}
              data-testid={`worker-mobile-tab-${tab.id}`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="text-xs mt-1">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute -top-1 right-2 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* New Job Popup */}
      {incomingJob && (
        <NewJobPopup
          job={incomingJob}
          onAccept={handleAcceptJob}
          onSchedule={handleScheduleJob}
          onDecline={handleDeclineJob}
          onClose={() => setIncomingJob(null)}
        />
      )}
    </div>
  );
};

export default WorkerMobileApp;
